import React, { useState } from 'react';
import { Edit2, MapPin, Trash2 } from 'lucide-react';
import type { Bed, MapMode, Plant, Zone } from '../../types';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { PlantForm } from '../forms/PlantForm';
import { PhotoTimeline } from '../PhotoTimeline';
import { NoteTimeline } from '../NoteTimeline';
import { STATUS_BG, STATUS_LABELS, TYPE_EMOJI, TYPE_LABELS, formatDate } from '../../lib/utils';

type Tab = 'info' | 'photos' | 'notes';

interface PlantDetailProps {
  plant: Plant;
  bed: Bed | undefined;
  zone: Zone | undefined;
  beds: Bed[];
  mapMode: MapMode;
  pendingX: number | null;
  pendingY: number | null;
  onSetMapMode: (m: MapMode) => void;
  onUpdate: (data: Partial<Plant>) => Promise<void>;
  onDelete: () => void;
  onClose: () => void;
}

export function PlantDetail({
  plant,
  bed,
  zone,
  beds,
  mapMode,
  pendingX,
  pendingY,
  onSetMapMode,
  onUpdate,
  onDelete,
  onClose,
}: PlantDetailProps) {
  const [tab, setTab] = useState<Tab>('info');
  const [editing, setEditing] = useState(false);

  return (
    <>
      {/* Header */}
      <div className="flex items-start gap-2 mb-3">
        <span className="text-2xl" aria-hidden="true">{TYPE_EMOJI[plant.plant_type]}</span>
        <div className="flex-1 min-w-0">
          <h2 className="font-serif text-lg font-semibold text-gray-900 dark:text-gray-100 leading-snug">{plant.common_name}</h2>
          {plant.botanical_name && (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">{plant.botanical_name}{plant.cultivar ? ` '${plant.cultivar}'` : ''}</p>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          <Button variant="ghost" size="sm" aria-label="Edit plant" onClick={() => setEditing(true)}><Edit2 size={15} /></Button>
          <Button variant="ghost" size="sm" aria-label="Delete plant" onClick={onDelete}><Trash2 size={15} className="text-red-500" /></Button>
        </div>
      </div>

      {/* Status + location */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Badge className={STATUS_BG[plant.status]}>{STATUS_LABELS[plant.status]}</Badge>
        <Badge className="bg-gray-100 text-gray-600">{TYPE_LABELS[plant.plant_type]}</Badge>
        {bed && <Badge className="bg-garden-100 text-garden-700">{bed.name}</Badge>}
        {zone && (
          <Badge style={{ background: zone.color + '33', color: zone.color.replace(/#/, '') }} className="">
            {zone.name}
          </Badge>
        )}
        {plant.x != null && (
          <Badge className="bg-gray-100 text-gray-500">
            <MapPin size={10} className="mr-0.5" />({Math.round(plant.x)}, {Math.round(plant.y!)})
          </Badge>
        )}
      </div>

      {/* Tabs */}
      <div role="tablist" className="flex border-b border-gray-100 dark:border-gray-800 mb-4">
        {(['info','photos','notes'] as Tab[]).map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium capitalize transition-colors border-b-2 -mb-px ${
              tab === t
                ? 'border-garden-600 text-garden-700 dark:text-garden-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div role="tabpanel" aria-label={tab}>
        {tab === 'info' && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <InfoRow label="Planted" value={formatDate(plant.planted_date)} />
            {plant.removed_date && <InfoRow label="Removed" value={formatDate(plant.removed_date)} />}
            {plant.sun_needs && <InfoRow label="Sun" value={plant.sun_needs} />}
            {plant.water_needs && <InfoRow label="Water" value={plant.water_needs} />}
            {plant.mature_height && <InfoRow label="Height" value={`${plant.mature_height} m`} />}
            {plant.mature_spread && <InfoRow label="Spread" value={`${plant.mature_spread} m`} />}
            {plant.notes && (
              <div className="col-span-2 mt-2 text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-sm whitespace-pre-wrap">
                {plant.notes}
              </div>
            )}
          </div>
        )}

        {tab === 'photos' && <PhotoTimeline entityType="plant" entityId={plant.id} />}
        {tab === 'notes' && <NoteTimeline entityType="plant" entityId={plant.id} />}
      </div>

      {/* Edit modal */}
      <Modal open={editing} onClose={() => setEditing(false)} title="Edit Plant" size="lg">
        <PlantForm
          initial={plant}
          beds={beds}
          mapMode={mapMode}
          pendingX={pendingX}
          pendingY={pendingY}
          onSetMapMode={onSetMapMode}
          onSubmit={async (data) => { await onUpdate(data); setEditing(false); }}
          onCancel={() => setEditing(false)}
          isEdit
        />
      </Modal>
    </>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-xs uppercase tracking-wide text-gray-400 font-medium">{label}</span>
      <p className="text-gray-800 dark:text-gray-200 font-medium">{value}</p>
    </div>
  );
}
